import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './FeaturedSubscriptions.css';

const FeaturedSubscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, [typeFilter]);

  const fetchSubscriptions = async () => {
    try {
      const params = typeFilter ? `?type=${encodeURIComponent(typeFilter)}` : '';
      const response = await axios.get(`${API_BASE_URL}/admin/featured-subscriptions${params}`);
      setSubscriptions(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch featured subscriptions');
      setLoading(false);
    }
  };

  const handleToggleActive = async (subscriptionId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingStatus(prev => ({ ...prev, [subscriptionId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/featured-subscriptions/${subscriptionId}`, {
        status: newStatus
      });
      
      // Update the subscription in the local state
      setSubscriptions(subscriptions.map(sub => 
        sub._id === subscriptionId 
          ? { ...sub, status: response.data.data.status }
          : sub
      ));
    } catch (error) {
      setError('Failed to update subscription status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [subscriptionId]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/featured-subscriptions/${id}`);
      fetchSubscriptions();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete subscription');
    }
  };

  if (loading && subscriptions.length === 0) {
    return (
      <Layout>
        <div className="subscriptions-container">
          <div className="subscriptions-header">
            <div className="subscriptions-header-content">
              <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '250px', height: '20px', borderRadius: '4px' }}></div>
            </div>
            <div className="skeleton skeleton-button" style={{ width: '200px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="subscriptions-table-container">
            <table className="subscriptions-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '16px', marginBottom: '4px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '90%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="subscriptions-container">
        <div className="subscriptions-header">
          <div className="subscriptions-header-content">
            <h1>Featured Subscriptions</h1>
            <p className="subscriptions-subtitle">Manage featured subscription plans (Hotel or SPA)</p>
            <div className="subscriptions-filters" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="type-filter">Type:</label>
              <select
                id="type-filter"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc' }}
              >
                <option value="">All</option>
                <option value="Hotel">Hotel</option>
                <option value="SPA">SPA</option>
              </select>
            </div>
          </div>
          <Link to="/sy-admin/featured-subscriptions/new" className="btn btn-primary">
            Add New Subscription
          </Link>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {subscriptions.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">⭐</div>
            <h2>No subscriptions found</h2>
            <p>Get started by adding your first featured subscription plan.</p>
            <Link to="/sy-admin/featured-subscriptions/new" className="btn btn-primary">
              Add Your First Subscription
            </Link>
          </div>
        ) : (
          <div className="subscriptions-table-container">
            <table className="subscriptions-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Duration</th>
                  <th>Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription) => {
                  const isTogglingStatus = togglingStatus[subscription._id];
                  return (
                    <tr key={subscription._id}>
                      <td>
                        <div className="subscription-name-cell">
                          <strong className="subscription-name-text">{subscription.name}</strong>
                          {subscription.description && (
                            <p className="table-description">{subscription.description}</p>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`type-badge type-${(subscription.type || 'Hotel').toLowerCase()}`}>
                          {subscription.type || 'Hotel'}
                        </span>
                      </td>
                      <td>
                        <div className="subscription-duration-cell">
                          {subscription.duration} {subscription.durationUnit}
                        </div>
                      </td>
                      <td>
                        <div className="subscription-price-cell">
                          {subscription.currency === 'INR' ? '₹' : subscription.currency} {subscription.price}
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          <label className="status-toggle-label">
                            <div className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={subscription.status === 'active'}
                                onChange={() => handleToggleActive(subscription._id, subscription.status)}
                                disabled={isTogglingStatus}
                                className="toggle-input"
                              />
                              <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                            </div>
                            <span className={`status-text ${subscription.status === 'active' ? 'status-active-text' : 'status-inactive-text'}`}>
                              {subscription.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => navigate(`/sy-admin/featured-subscriptions/edit/${subscription._id}`)}
                            className="btn btn-edit btn-icon-only"
                            title="Edit Subscription"
                          >
                            <span className="btn-icon">✏️</span>
                          </button>
                          <button
                            onClick={() => handleDelete(subscription._id)}
                            className="btn btn-delete btn-icon-only"
                            title="Delete Subscription"
                          >
                            <span className="btn-icon">🗑️</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FeaturedSubscriptions;

